import React from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import { useWebSocketContext } from '../../../services/websocket/context/WebSocketContext';
import { WebSocketMessage } from '../../../services/websocket/hooks/useWebSocket';
import PlaceDetailsPanel from '../../components/maps/PlaceDetailsPanel';
import ActionRequestCard from '../../components/chat/ActionRequestCard';
import TripDraftCard, { TripDraftShape } from '../../components/chat/TripDraftCard';
import { ActionRequest, ActionResolution } from '../../../services/trips/actionRequests';
import { normalizePlaceDetails, PlaceDetails, PLACE_DETAIL_FIELDS } from '../../components/maps/placeDetails';
import '../../components/maps/MapPlaceSidebar.css';
import './AskAiPage.css';

interface LocationRecommendation {
  name: string;
  place_id: string;
}

interface AgentResponse {
  intent: string;
  confidence: number;
  message: string;
  locations_recommendations?: LocationRecommendation[];
  trip_draft?: TripDraftShape;
  action_request?: ActionRequest;
}

interface ChatMessage {
  id: string;
  message: string;
  isUser: boolean;
  timestamp: Date;
  isLoading?: boolean;
  isError?: boolean;
  intent?: string;
  locations_recommendations?: LocationRecommendation[];
  trip_draft?: TripDraftShape;
  action_request?: ActionRequest;
  actionResolution?: ActionResolution;
}

const promptChips = [
  { label: 'Weekend in the mountains', icon: '🏔️' },
  { label: 'Best food cities in Asia', icon: '🍜' },
  { label: 'Family-friendly beaches', icon: '🏖️' },
  { label: 'Cheap European getaways', icon: '🗺️' },
];

const welcomeMessage =
  "Hi! I'm your EnRoute AI — ask me anything about destinations, activities, food, or logistics. I'll drop suggestions right on the map.";

const parseAgentResponse = (text: string): { response: AgentResponse | null; isError: boolean } => {
  if (!text || typeof text !== 'string') {
    return { response: null, isError: false };
  }

  const trimmed = text.trim();
  if (!trimmed.startsWith('{') || !trimmed.endsWith('}')) {
    return { response: null, isError: false };
  }

  try {
    const parsed = JSON.parse(trimmed) as any;
    const response = parsed.response?.intent ? parsed.response : parsed.intent ? parsed : null;

    if (response?.intent && response.message !== undefined && response.confidence !== undefined) {
      return { response, isError: false };
    }
  } catch {
    return { response: null, isError: false };
  }

  return { response: null, isError: false };
};

const AskAiPage: React.FC = () => {
  const webSocket = useWebSocketContext();
  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = React.useRef<any>(null);
  const recommendationMarkersRef = React.useRef<any[]>([]);
  const chatMessagesEndRef = React.useRef<HTMLDivElement | null>(null);
  const responseTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  const [chatMessages, setChatMessages] = React.useState<ChatMessage[]>([]);
  const [currentInput, setCurrentInput] = React.useState('');
  const [isWaitingForResponse, setIsWaitingForResponse] = React.useState(false);
  const [conversationId, setConversationId] = React.useState('');
  const [mapType, setMapType] = React.useState<'roadmap' | 'satellite'>('roadmap');
  const [suggestedPlacesCount, setSuggestedPlacesCount] = React.useState(0);
  const [selectedPlace, setSelectedPlace] = React.useState<PlaceDetails | null>(null);
  const [isLoadingPlace, setIsLoadingPlace] = React.useState(false);
  const [selectedPlaceId, setSelectedPlaceId] = React.useState<string | null>(null);
  const [userLocation, setUserLocation] = React.useState<{ lat: number; lng: number } | null>(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const hasApiKey = Boolean(googleMapsApiKey);

  const finishAssistantMessage = React.useCallback((
    message: string,
    isError = false,
    agentResponse?: AgentResponse
  ) => {
    setChatMessages((current) => {
      const updated = [...current];
      const lastMessageIndex = updated.length - 1;
      const messageData: Partial<ChatMessage> = {
        message,
        isLoading: false,
        isError,
        intent: agentResponse?.intent,
        locations_recommendations: agentResponse?.locations_recommendations,
        trip_draft: agentResponse?.trip_draft,
        action_request: agentResponse?.action_request,
      };

      if (lastMessageIndex >= 0 && updated[lastMessageIndex].isLoading) {
        updated[lastMessageIndex] = {
          ...updated[lastMessageIndex],
          ...messageData,
        };
        return updated;
      }

      return [
        ...updated,
        {
          id: `assistant-${Date.now()}`,
          message,
          isUser: false,
          timestamp: new Date(),
          isLoading: false,
          isError,
          intent: agentResponse?.intent,
          locations_recommendations: agentResponse?.locations_recommendations,
          trip_draft: agentResponse?.trip_draft,
          action_request: agentResponse?.action_request,
        },
      ];
    });

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }
    setIsWaitingForResponse(false);
  }, []);

  const clearRecommendationMarkers = React.useCallback(() => {
    recommendationMarkersRef.current.forEach((marker) => {
      marker.setMap(null);
    });
    recommendationMarkersRef.current = [];
  }, []);

  const fetchPlaceDetails = React.useCallback((placeId: string) => {
    if (!mapInstanceRef.current || !window.google?.maps?.places) return;

    setIsLoadingPlace(true);
    setSelectedPlaceId(placeId);

    const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
    service.getDetails(
      {
        placeId,
        fields: PLACE_DETAIL_FIELDS,
      },
      (place: PlaceDetails | null, status: string) => {
        setIsLoadingPlace(false);

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          setSelectedPlace(null);
          return;
        }

        setSelectedPlace(normalizePlaceDetails(
          place as PlaceDetails & { current_opening_hours?: PlaceDetails['opening_hours'] }
        ));

        if (place.geometry?.location && mapInstanceRef.current) {
          mapInstanceRef.current.panTo(place.geometry.location);
          mapInstanceRef.current.setZoom(15);
        }
      }
    );
  }, []);

  const addRecommendationMarkers = React.useCallback((recommendations: LocationRecommendation[]) => {
    if (!mapInstanceRef.current || !window.google?.maps?.Marker || !window.google?.maps?.places) return;

    clearRecommendationMarkers();

    const bounds = new window.google.maps.LatLngBounds();
    let placedCount = 0;

    recommendations.forEach((recommendation) => {
      if (!recommendation.place_id) return;

      const service = new window.google.maps.places.PlacesService(mapInstanceRef.current);
      service.getDetails(
        {
          placeId: recommendation.place_id,
          fields: ['geometry', 'name'],
        },
        (place: any, status: string) => {
          if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            return;
          }

          const marker = new window.google.maps.Marker({
            map: mapInstanceRef.current,
            position: place.geometry.location,
            title: recommendation.name,
          });

          marker.addListener('click', () => {
            fetchPlaceDetails(recommendation.place_id);
          });

          recommendationMarkersRef.current.push(marker);
          bounds.extend(place.geometry.location);
          placedCount += 1;
          setSuggestedPlacesCount(placedCount);

          if (placedCount === recommendations.filter((item) => item.place_id).length) {
            mapInstanceRef.current.fitBounds(bounds, 48);
          }
        }
      );
    });
  }, [clearRecommendationMarkers, fetchPlaceDetails]);

  React.useEffect(() => {
    if (!hasApiKey || !mapRef.current || mapInstanceRef.current) return undefined;

    let interval: NodeJS.Timeout | null = null;

    const initializeMap = () => {
      if (!window.google?.maps?.Map || !mapRef.current || mapInstanceRef.current) return;

      const center = userLocation || { lat: 40.749933, lng: -73.98633 };
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center,
        zoom: 12,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
      });

      mapInstanceRef.current = mapInstance;
    };

    if (window.google?.maps?.Map) {
      initializeMap();
    } else {
      interval = setInterval(() => {
        if (window.google?.maps?.Map) {
          if (interval) clearInterval(interval);
          initializeMap();
        }
      }, 100);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [hasApiKey, userLocation]);

  React.useEffect(() => {
    if (!mapInstanceRef.current) return;
    mapInstanceRef.current.setMapTypeId(mapType);
  }, [mapType]);

  React.useEffect(() => {
    if (!('geolocation' in navigator)) return;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        setUserLocation(location);
        if (mapInstanceRef.current) {
          mapInstanceRef.current.setCenter(location);
        }
      },
      () => undefined,
      { enableHighAccuracy: false, timeout: 8000 }
    );
  }, []);

  React.useEffect(() => {
    chatMessagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  React.useEffect(() => {
    const latestWithRecommendations = [...chatMessages]
      .reverse()
      .find((message) => message.locations_recommendations?.length);

    if (latestWithRecommendations?.locations_recommendations) {
      addRecommendationMarkers(latestWithRecommendations.locations_recommendations);
    }
  }, [chatMessages, addRecommendationMarkers]);

  React.useEffect(() => {
    return webSocket.subscribe((message: WebSocketMessage) => {
      const incomingConversationId = message.conversation_id || (message as any).conversationId;

      if (incomingConversationId) {
        setConversationId(incomingConversationId);
      }

      if (conversationId && incomingConversationId && incomingConversationId !== conversationId) {
        return;
      }

      const status = (message as any).status || (message as any).body?.status || (message as any).response?.status;
      const text =
        (message as any).text ||
        (message as any).body?.text ||
        (message as any).response?.text ||
        (message as any).message ||
        (message as any).response?.message;
      const error = (message as any).error || (message as any).body?.error || (message as any).response?.error;

      if (status === 'processing' || message.message === 'Connection accepted') {
        return;
      }

      if (error) {
        finishAssistantMessage(`Error: ${error}`, true);
        return;
      }

      if (status === 'complete' || text) {
        let responseText = text || '';
        let agentResponse: AgentResponse | null = null;

        if ((message as any).response && typeof (message as any).response === 'object' && (message as any).response.intent) {
          agentResponse = (message as any).response as AgentResponse;
          responseText = agentResponse.message;
        } else {
          const parsed = parseAgentResponse(responseText);
          agentResponse = parsed.response;
          responseText = agentResponse?.message || responseText;
        }

        finishAssistantMessage(responseText, false, agentResponse || undefined);
      }
    });
  }, [conversationId, finishAssistantMessage, webSocket]);

  React.useEffect(() => () => {
    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
    }
    clearRecommendationMarkers();
  }, [clearRecommendationMarkers]);

  const handleSendMessage = async (message = currentInput) => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage || isWaitingForResponse) return;

    setCurrentInput('');
    setChatMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        message: trimmedMessage,
        isUser: true,
        timestamp: new Date(),
      },
      {
        id: `loading-${Date.now()}`,
        message: '',
        isUser: false,
        timestamp: new Date(),
        isLoading: true,
      },
    ]);
    setIsWaitingForResponse(true);

    try {
      await webSocket.sendMessage(trimmedMessage, conversationId || undefined, {
        user_time: new Date().toISOString(),
        user_location: userLocation
          ? { lat: userLocation.lat.toString(), lng: userLocation.lng.toString() }
          : undefined,
        // REQ-15: the place the user is viewing — ground truth for the agent,
        // so "is it open now?" needs no second Places lookup.
        selected_place: selectedPlace
          ? {
              place_id: selectedPlaceId || undefined,
              name: selectedPlace.name,
              address: selectedPlace.formatted_address,
              rating: selectedPlace.rating,
              user_ratings_total: selectedPlace.user_ratings_total,
              price_level: selectedPlace.price_level,
              phone: selectedPlace.formatted_phone_number,
              website: selectedPlace.website,
              open_now: selectedPlace.opening_hours?.open_now,
              weekday_hours: selectedPlace.opening_hours?.weekday_text,
              types: selectedPlace.types,
            }
          : undefined,
      });

      responseTimeoutRef.current = setTimeout(() => {
        finishAssistantMessage('The assistant is taking longer than expected. Please try again.', true);
      }, 60000);
    } catch (err: any) {
      finishAssistantMessage(err.message || 'Could not send message to the assistant.', true);
    }
  };

  const resolveActionRequest = React.useCallback((messageId: string, resolution: ActionResolution, note: string) => {
    setChatMessages((current) => {
      const updated = current.map((m) => (m.id === messageId ? { ...m, actionResolution: resolution } : m));
      if (resolution !== 'dismissed') {
        updated.push({
          id: `action-${resolution}-${Date.now()}`,
          message: resolution === 'confirmed' ? `✓ ${note}` : `⚠ ${note}`,
          isUser: false,
          timestamp: new Date(),
          isError: resolution === 'failed',
        });
      }
      return updated;
    });
  }, []);

  const handleRecommendationSelect = (recommendation: LocationRecommendation) => {
    if (!recommendation.place_id) return;
    fetchPlaceDetails(recommendation.place_id);
  };

  const handleNewChat = () => {
    setChatMessages([]);
    setConversationId('');
    setSelectedPlace(null);
    setSelectedPlaceId(null);
    setSuggestedPlacesCount(0);
    clearRecommendationMarkers();
  };

  const statusLabel = webSocket.isConnected
    ? 'Ask anything, anywhere'
    : webSocket.isConnecting
    ? 'Connecting...'
    : 'Offline';

  return (
    <DashboardLayout activeSection="ask-ai" wide>
      <div className="v2-ask-ai-page">
        <section className="v2-ask-ai-chat" aria-label="Ask AI chat">
          <header className="v2-ask-ai-chat__header">
            <button type="button" className="v2-ask-ai-chat__session" onClick={handleNewChat}>
              New chat
              <span aria-hidden>▾</span>
            </button>
            <p className="v2-ask-ai-chat__status">
              <span className={`v2-ask-ai-chat__status-dot ${webSocket.isConnected ? 'v2-ask-ai-chat__status-dot--online' : ''}`} />
              {statusLabel}
            </p>
          </header>

          <div className="v2-ask-ai-chat__messages">
            <AssistantMessage>{welcomeMessage}</AssistantMessage>

            {chatMessages.map((message) => (
              message.isUser ? (
                <UserMessage key={message.id}>{message.message}</UserMessage>
              ) : (
                <React.Fragment key={message.id}>
                  <AssistantMessage isLoading={message.isLoading} isError={message.isError}>
                    {message.isLoading ? 'Thinking...' : message.message}
                  </AssistantMessage>
                  {!message.isLoading && message.action_request && (
                    <ActionRequestCard
                      request={message.action_request}
                      resolution={message.actionResolution}
                      onResolve={(resolution, note) => resolveActionRequest(message.id, resolution, note)}
                    />
                  )}
                  {!message.isLoading && !message.action_request && message.trip_draft && message.intent === 'CONFIRM_TRIP' && (
                    <TripDraftCard
                      draft={message.trip_draft}
                      resolution={message.actionResolution}
                      onResolve={(resolution, note) => resolveActionRequest(message.id, resolution, note)}
                    />
                  )}
                  {message.locations_recommendations && message.locations_recommendations.length > 0 && (
                    <div className="v2-ask-ai-chat__recommendations">
                      {message.locations_recommendations.map((recommendation) => (
                        <button
                          type="button"
                          key={`${message.id}-${recommendation.place_id || recommendation.name}`}
                          className={`v2-ask-ai-chat__recommendation ${
                            selectedPlaceId === recommendation.place_id ? 'v2-ask-ai-chat__recommendation--active' : ''
                          }`}
                          onClick={() => handleRecommendationSelect(recommendation)}
                        >
                          {recommendation.name}
                        </button>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              )
            ))}
            <div ref={chatMessagesEndRef} />
          </div>

          <div className="v2-ask-ai-chat__prompts">
            {promptChips.map((chip) => (
              <button
                type="button"
                key={chip.label}
                onClick={() => handleSendMessage(chip.label)}
                disabled={isWaitingForResponse}
              >
                <span aria-hidden>{chip.icon}</span>
                {chip.label}
              </button>
            ))}
          </div>

          <div className="v2-ask-ai-chat__input">
            <button type="button" className="v2-ask-ai-chat__attach" aria-label="Attach file">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <input
              type="text"
              value={currentInput}
              onChange={(event) => setCurrentInput(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleSendMessage();
                }
              }}
              placeholder="Ask about a destination, activity, or trip idea..."
              aria-label="Ask about a destination, activity, or trip idea"
              disabled={isWaitingForResponse}
            />
            <button
              type="button"
              className="v2-ask-ai-chat__send"
              onClick={() => handleSendMessage()}
              disabled={!currentInput.trim() || isWaitingForResponse || !webSocket.isConnected}
              aria-label="Send message"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                <path d="m22 2-7 20-4-9-9-4Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M22 2 11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
        </section>

        <aside className="v2-map-sidebar" aria-label="Map and place details">
          <div className="v2-map-card">
            {hasApiKey ? (
              <>
                <div ref={mapRef} className="v2-map-card__canvas" />
                <div className="v2-map-card__controls">
                  <div className="v2-map-card__type">
                    <button
                      type="button"
                      className={mapType === 'roadmap' ? 'v2-map-card__type--active' : undefined}
                      onClick={() => setMapType('roadmap')}
                    >
                      Map
                    </button>
                    <button
                      type="button"
                      className={mapType === 'satellite' ? 'v2-map-card__type--active' : undefined}
                      onClick={() => setMapType('satellite')}
                    >
                      Satellite
                    </button>
                  </div>
                  <div className="v2-map-card__zoom">
                    <button type="button" aria-label="Zoom in" onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) + 1)}>+</button>
                    <button type="button" aria-label="Zoom out" onClick={() => mapInstanceRef.current?.setZoom((mapInstanceRef.current.getZoom() || 12) - 1)}>−</button>
                  </div>
                </div>
                <div className="v2-map-card__badge">
                  <span aria-hidden>📍</span>
                  {suggestedPlacesCount} {suggestedPlacesCount === 1 ? 'place' : 'places'} suggested
                </div>
              </>
            ) : (
              <div className="v2-map-card__fallback">
                <p>Map preview unavailable</p>
                <span>Add REACT_APP_GOOGLE_MAPS_API_KEY to enable the map.</span>
              </div>
            )}
          </div>

          <PlaceDetailsPanel place={selectedPlace} isLoading={isLoadingPlace} />
        </aside>
      </div>
    </DashboardLayout>
  );
};

const AssistantMessage: React.FC<{ children: React.ReactNode; isLoading?: boolean; isError?: boolean }> = ({
  children,
  isLoading = false,
  isError = false,
}) => (
  <div className={`v2-ask-ai-message v2-ask-ai-message--assistant ${isError ? 'v2-ask-ai-message--error' : ''}`}>
    <span className="v2-ask-ai-message__avatar" aria-hidden>+</span>
    <p>{children}{isLoading ? '' : ''}</p>
  </div>
);

const UserMessage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="v2-ask-ai-message v2-ask-ai-message--user">
    <p>{children}</p>
  </div>
);

export default AskAiPage;
